import React from "react";
import { CacheProvider } from "@emotion/react";
import type { EmotionCache } from "@emotion/cache";
import { trackingProviderCaptureClick } from "@origin-digital/reporting-client";
import {
  OriginThemeProvider,
  TrackingProvider,
} from "@origin-digital/ods-core";
import {
  LinkComponentProps,
  OriginThemeMode,
  Theme,
} from "@origin-digital/ods-types";
import { parseMarkdownEvent } from "@origin-digital/event-dispatcher";
import { UserContextProvider } from "@origin-digital/user-context-provider";
import {
  FeatureFlagContextProvider,
  useFeatureFlag,
} from "@origin-digital/feature-flags";
import { EnvironmentNames } from "@origin-digital/platform-enums";
import { useTokenShield } from "@origin-digital/token-shield";
import { isOnServer } from "@origin-digital/tal-common";
import {
  THEME,
  FEATURE_FLAGS,
  getDefaultFeatures,
} from "@origin-digital/tal-client-common";
import {
  FontStyles,
  ResetStyles,
  IconFontStyles,
  BrazeStyles,
  BrazeStylesDark,
} from "../styles/global";
import { useThemeListener } from "../hooks/useThemeListener";
import { SkipLinkTargetStyles } from "./SkipLink";

type Props = {
  setupFonts?: boolean;
  children: React.ReactNode;
  muiTheme?: Theme;
};

const MeshLink = React.forwardRef<HTMLAnchorElement, LinkComponentProps>(
  (props, ref) => {
    const { href, onClick } = parseMarkdownEvent(props.href);
    const { children, ...rest } = props;
    return (
      <a
        ref={ref}
        {...rest}
        href={href}
        onClick={(e) => {
          if (onClick) {
            onClick(e);
          }
          return rest.onClick && rest.onClick(e);
        }}
      >
        {children}
      </a>
    );
  }
);

/*
 * This messy pair of components is used to support feature toggling of TokenShield with environment config.
 * Because it's all implemented as hooks, conditionally toggling them isn't possible, so we wrap it in some components to make it easier
 * TODO: Delete this after tokenshield rollout is complete, and call hook in rootwrapper directly
 */
export const TokenShieldToggler: React.FC = () => {
  const enabled = useFeatureFlag(FEATURE_FLAGS.TOKEN_SHIELD);
  const TokenShield: React.FC = () => {
    useTokenShield();
    return <></>;
  };
  if (enabled) {
    return <TokenShield />;
  }
  return <></>;
};

export const BrazeStylesToggler: React.FC<{
  currentTheme: OriginThemeMode;
  muiTheme?: Theme;
}> = ({ currentTheme, muiTheme }) => {
  const activeTheme = muiTheme?.palette?.mode ?? currentTheme;
  return activeTheme === THEME.DARK ? <BrazeStylesDark /> : <BrazeStyles />;
};

const ThemeStyles: React.FC<{
  setupFonts: boolean;
  muiTheme?: Theme;
}> = ({ setupFonts, muiTheme }) => {
  const currentTheme = useThemeListener();

  if (!setupFonts) {
    return null;
  }

  return (
    <>
      <FontStyles />
      <ResetStyles />
      <IconFontStyles />
      <BrazeStylesToggler currentTheme={currentTheme} muiTheme={muiTheme} />
      <SkipLinkTargetStyles />
    </>
  );
};

export default ({ setupFonts = true, children, muiTheme }: Props) => {
  // Guard against SSR error in tal-core
  const env =
    typeof window === "undefined"
      ? undefined
      : (window?.oetal?.env?.name as EnvironmentNames);

  // Use cache only in real browser: typeof window guards Node; isOnServer() guards JSDOM (appSSR).
  const emotionCache: EmotionCache | null =
    typeof window !== "undefined" && !isOnServer()
      ? (window as Window & { __emotionCache?: EmotionCache }).__emotionCache ??
        null
      : null;

  const content = (
    <>
      <ThemeStyles setupFonts={setupFonts} muiTheme={muiTheme} />
      <OriginThemeProvider
        //@ts-ignore fails when updating to ts 4
        linkComponent={MeshLink}
        generatorClassNameOptions={{ seed: "tal" }}
        enableThemeSwitching
        useExternalEmotionCache={true} // TAL: Emotion externalised; cache from tal-core SSR / tal-client globals
        injectGlobalStyles={false}
        {...(muiTheme ? { muiTheme } : {})}
      >
        <FeatureFlagContextProvider featureFlags={getDefaultFeatures(env)}>
          <>
            <TokenShieldToggler />
            <UserContextProvider>
              <TrackingProvider
                onTrackingCapture={trackingProviderCaptureClick}
              >
                {children}
              </TrackingProvider>
            </UserContextProvider>
          </>
        </FeatureFlagContextProvider>
      </OriginThemeProvider>
    </>
  );

  // On client we have window.__emotionCache and must provide it. On SSR (Node or JSDOM)
  // tal-core already wraps the tree in CacheProvider in RenderStrategies.
  return emotionCache ? (
    <CacheProvider value={emotionCache}>{content}</CacheProvider>
  ) : (
    content
  );
};
